import {
	TabContent,
	TabItem,
	Table,
	Tabs,
	TabsList,
	Time,
	Dropdown,
	Icons,
	Badge,
	Text,
	Stack,
	Box,
	Inline,
	Select,
} from "@sembark-travel/ui/base"
import { IListResponse } from "@sembark-travel/xhr"
import { IComment } from "./store"
import { Link, useLocationQuery } from "@sembark-travel/ui/router"
import { generatePath } from "../router-utils"
import {
	ListView,
	Search,
	TSearchParams,
	areAdvancedFiltersAppliedDefault,
	useSearch,
} from "@sembark-travel/ui/list"
import { useEffect, useMemo } from "react"
import {
	dateToUTCString,
	endOf,
	isBefore,
	startOf,
	subtractUnit,
} from "@sembark-travel/datetime-utils"
import { Optional } from "utility-types"
import { SelectField, useFieldValue } from "@sembark-travel/ui/form"
import { SelectUsers } from "../Users"
import {
	IUser,
	PERMISSIONS,
	useAuthManagedUsers,
	useAuthUser,
	useCheckPermissions,
} from "../Auth"
import { SelectTripDestination, TTripDestination } from "../TripDestinations"
import { TripSourceCell, TripTourist } from "../Trips"

type TFilters = TSearchParams & {
	activeTab: "today" | "yesterday" | "overdue" | "upcoming" | "all"
	assigned_to?: Array<IUser>
	trip_destinations?: Array<TTripDestination>
	created_by?: Array<IUser>
	follow_status?: Array<{ name: string; id: string }>
}

type TFiltersInQuery = Omit<
	Optional<TFilters, "activeTab">,
	"assigned_to" | "trip_destinations" | "created_by" | "follow_status"
> & {
	assigned_to?: Array<string>
	trip_destinations?: Array<string>
	created_by?: Array<string>
	follow_status?: Array<string>
}

function queryToFilteres(query: TFiltersInQuery): TFilters {
	const {
		activeTab,
		assigned_to,
		trip_destinations,
		created_by,
		follow_status,
		...others
	} = query
	const filters: TFilters = {
		activeTab: activeTab || "today",
		...others,
	}
	if (assigned_to) {
		filters.assigned_to = assigned_to.map((s) => {
			const [id, ...name] = s.split("#")
			return {
				id,
				name: name.join("#"),
			} as never as IUser
		})
	}
	if (follow_status) {
		filters.follow_status = follow_status.map((s) => {
			const [id, ...name] = s.split("#")
			return {
				id,
				name: name.join("#"),
			} as never as { name: string; id: string }
		})
	}
	if (trip_destinations) {
		filters.trip_destinations = trip_destinations.map((s) => {
			const [id, ...name] = s.split("#")
			return {
				id,
				name: name.join("#"),
			} as never as TTripDestination
		})
	}
	if (created_by) {
		filters.created_by = created_by.map((s) => {
			const [id, ...name] = s.split("#")
			return {
				id,
				name: name.join("#"),
			} as never as IUser
		})
	}
	return filters
}

function filtersToQuery(filters: TFilters): TFiltersInQuery {
	const {
		assigned_to,
		trip_destinations,
		created_by,
		follow_status,
		...others
	} = filters
	const query: TFiltersInQuery = {
		...others,
	}
	if (assigned_to) {
		query.assigned_to = assigned_to.map((s) => `${s.id}#${s.name}`)
	}
	if (follow_status) {
		query.follow_status = follow_status.map((s) => `${s.id}#${s.name}`)
	}
	if (trip_destinations) {
		query.trip_destinations = trip_destinations.map((s) => `${s.id}#${s.name}`)
	}
	if (created_by) {
		query.created_by = created_by.map((s) => `${s.id}#${s.name}`)
	}

	return query
}
const resetParams = (params: TFilters) => {
	return {
		activeTab: params.activeTab,
	}
}

export function CommentsList() {
	const [query, setQuery] = useLocationQuery({
		fromQuery: queryToFilteres,
		toQuery: filtersToQuery,
	})
	const [params, setParams] = useSearch(query)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	const today = new Date()
	const { hasPermission } = useCheckPermissions()
	const managed_users = useAuthManagedUsers()
	const canViewAssignedToOthers =
		hasPermission(PERMISSIONS.MANAGE_USERS) || managed_users.length
	const { user } = useAuthUser()
	return (
		<>
			<Search
				initialParams={params}
				resetParams={resetParams}
				onSearch={(params) => setParams({ ...params, page: 1 })}
				title="All Tasks"
				areAdvancedFiltersApplied={(params) => {
					const { activeTab, ...others } = params
					return areAdvancedFiltersAppliedDefault(others)
				}}
				Filters={canViewAssignedToOthers ? Filters : undefined}
			>
				{({ searchParams, setSearchParams }) => (
					<>
						<Tabs>
							<TabsList>
								<TabItem
									active={searchParams.activeTab === "today"}
									onClick={() =>
										setSearchParams({
											activeTab: "today",
										})
									}
								>
									Today
								</TabItem>
								<TabItem
									active={searchParams.activeTab === "yesterday"}
									onClick={() =>
										setSearchParams({
											activeTab: "yesterday",
											follow_status: params.follow_status?.filter(
												(s) => s.id !== "upcoming"
											),
										})
									}
								>
									Yesterday
								</TabItem>
								<TabItem
									active={searchParams.activeTab === "overdue"}
									onClick={() => {
										setSearchParams({
											activeTab: "overdue",
											follow_status: params.follow_status?.filter(
												(s) => s.id !== "upcoming" && s.id !== "done"
											),
										})
									}}
								>
									Overdue
								</TabItem>
								<TabItem
									active={searchParams.activeTab === "upcoming"}
									onClick={() => {
										setSearchParams({
											activeTab: "upcoming",
											follow_status: params.follow_status?.filter(
												(s) => s.id !== "overdue" && s.id !== "done"
											),
										})
									}}
								>
									Upcoming
								</TabItem>
								<TabItem
									active={searchParams.activeTab === "all"}
									onClick={() => {
										setSearchParams({
											activeTab: "all",
										})
									}}
								>
									All
								</TabItem>
							</TabsList>
							<TabContent>
								<ListView
									pageKey="follow-ups-comments"
									params={params}
									fetch={(
										xhr,
										{
											activeTab,
											assigned_to,
											created_by,
											trip_destinations,
											follow_status,
											...params
										}
									) =>
										xhr
											.get<IListResponse<IComment>>("/comments", {
												params: {
													...params,
													statuses: follow_status?.map((s) => s.id),
													menu_status: activeTab,
													due_after:
														activeTab === "today"
															? dateToUTCString(startOf(new Date(), "day"))
															: activeTab === "yesterday"
																? dateToUTCString(
																		startOf(
																			subtractUnit(new Date(), 1, "day"),
																			"day"
																		)
																	)
																: null,
													due_before:
														activeTab === "today"
															? dateToUTCString(endOf(today, "day"))
															: activeTab === "yesterday"
																? dateToUTCString(
																		endOf(
																			subtractUnit(new Date(), 1, "day"),
																			"day"
																		)
																	)
																: null,
													assigned_to: assigned_to?.map((s) => s.id),
													trip_destinations: trip_destinations?.map(
														(s) => s.id
													),
													created_by: created_by?.map((s) => s.id),
													include: "with_trip",
												},
											})
											.then((resp) => resp.data)
									}
									onPageChange={(page) => setParams({ ...params, page })}
								>
									{({ items, xhr, refresh }) => (
										<Table
											bordered
											hover
											responsive
											headers={[
												"Due At",
												"Description",
												"Trip Source",
												"Guest",
												"For",
												"Created by",
												"Assigned To",
												"",
											]}
											alignCols={{ 7: "right" }}
											rows={items.map((d) => [
												d.due_at ? (
													<Inline gap={"1"}>
														<Time
															timestamp={d.due_at}
															format={
																["yesterday", "today"].includes(
																	searchParams.activeTab
																)
																	? "hh:mm A"
																	: "hh:mm A dddd D MMM"
															}
															whiteSpace="nowrap"
														/>
														<Box>
															{isBefore(d.due_at) &&
															!d.resolved_at &&
															["yesterday", "today", "all"].includes(
																searchParams.activeTab
															) ? (
																<Badge outlined>Overdue</Badge>
															) : null}
														</Box>
													</Inline>
												) : null,
												<Text
													maxWidth="sm"
													color={d.resolved_at ? "success" : undefined}
													whiteSpace="preserveLine"
												>
													{d.body}
												</Text>,
												...(d.commentable_type !== "tenants" && d?.trip
													? [
															<TripSourceCell trip={d.trip} />,
															<TripTourist trip={d.trip} />,
														]
													: [null, null]),
												d.commentable_type === "trips" ? (
													<Link
														to={generatePath("/trips/:tripId", {
															tripId: String(d.commentable_id),
														})}
														color="accent"
														anchored
													>
														Trip#{d.commentable_id}
													</Link>
												) : d.commentable_type === "instalments" ? (
													<Link
														to={generatePath(
															"/payments/instalments/:instalmentId",
															{
																instalmentId: String(d.commentable_id),
															}
														)}
														anchored
														color="accent"
													>
														Instalment#{d.commentable_id}
													</Link>
												) : d.commentable_type === "tenants" ? (
													<Link
														to={generatePath(
															"/admin/tenants/:tenantId/comments",
															{
																tenantId: String(d.commentable_id),
															}
														)}
														anchored
														color="accent"
													>
														Tenant#{d.commentable_id}
													</Link>
												) : null,

												<Text>
													{d.created_by?.id === user?.id
														? "Me"
														: d.created_by?.name}{" "}
													on <Time timestamp={d.created_at} format="DD MMM" />
												</Text>,
												<Text>
													{
														d.assigned_to?.id === user?.id &&
														d.created_by?.id !== d.assigned_to?.id
															? "Me" // someone assign to & login by me
															: !d.assigned_to && d.created_by?.id === user?.id
																? "Self" // login by me & created by me & unassigned
																: d.assigned_to?.name || d.created_by.name // fallback assigned to name otherwise created by name
													}
												</Text>,

												d.needs_resolution ? (
													<Dropdown alignRight noCaret>
														<Dropdown.ToggleButton size="sm" level="tertiary">
															<Icons.DotsVertical />
														</Dropdown.ToggleButton>
														<Dropdown.Menu>
															<Dropdown.MenuItem
																onClick={() => {
																	if (
																		window.confirm(
																			"Please confirm that this is resolved.\n\nThis action CAN NOT be reverted."
																		)
																	) {
																		xhr
																			.post("/resolved-comments", {
																				items: [d.id],
																			})
																			.then(() => refresh())
																			.catch((error) => {
																				const e = error as Error
																				alert(
																					e.message ||
																						"Something went wrong. Please try after sometime"
																				)
																			})
																	}
																}}
															>
																Mark as Resolved
															</Dropdown.MenuItem>
														</Dropdown.Menu>
													</Dropdown>
												) : (
													<Stack gap="px">
														<Box>
															<Time timestamp={d.resolved_at}>
																<Badge success>Done</Badge>
															</Time>
														</Box>
														{canViewAssignedToOthers && d.resolved_by ? (
															<Text fontSize={"sm"} color="muted">
																by {d.resolved_by?.name}
															</Text>
														) : null}
													</Stack>
												),
											])}
										/>
									)}
								</ListView>
							</TabContent>
						</Tabs>
					</>
				)}
			</Search>
		</>
	)
}

const followupStatus: Array<{ name: string; id: string; tabs: Array<string> }> =
	[
		{ name: "Upcoming", id: "upcoming", tabs: ["today", "all", "upcoming"] },
		{
			name: "Overdue",
			id: "overdue",
			tabs: ["today", "all", "yesterday", "overdue"],
		},
		{ name: "Done", id: "done", tabs: ["today", "all", "yesterday"] },
	]

function Filters() {
	const { value: tab } = useFieldValue<string>("activeTab")
	const followupStatusOptions = useMemo(
		() =>
			followupStatus
				.filter((status) => status.tabs.includes(tab))
				.map(({ name, id }) => {
					return { name, id }
				}),
		[tab]
	)
	return (
		<Stack gap="4">
			<SelectField
				select={SelectUsers}
				name="assigned_to"
				label="Assigned To"
				fetchOnMount
				multiple
				searchOnlyByName={1}
			/>
			<SelectField
				name="trip_destinations"
				select={SelectTripDestination}
				label="Destinations"
				fetchOnMount
				multiple
			/>
			<SelectField
				select={SelectUsers}
				name="created_by"
				label="Created By"
				fetchOnMount
				multiple
				searchOnlyByName={1}
			/>
			<SelectField
				select={Select}
				name="follow_status"
				label="Task Status"
				options={followupStatusOptions}
				multiple
			/>
		</Stack>
	)
}
